import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { FormSection, DefinitionList, DataPair, Notice } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { getProcurement, getProcurementAttachmentSignedUrl, Procurement } from '../../services/procurementService';
import { formatDate, formatDateTime } from '../../dateFormat';

const ConsumableProcurementDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<Procurement | null>(null);
  const [attachmentError, setAttachmentError] = useState('');

  const handleOpenAttachment = async (attachmentId: string) => {
    if (!id) return;
    setAttachmentError('');
    try {
      const { signedUrl } = await getProcurementAttachmentSignedUrl(id, attachmentId);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      setAttachmentError(
        t('consumables.procurement.detail.attachmentError', {
          defaultValue: 'Could not open attachment. Please try again.',
        }),
      );
    }
  };

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.procurement.title'), to: '/consumables/procurement' },
    { label: doc?.referenceNumber ?? '' },
  ]);

  useEffect(() => {
    if (!id) return;
    getProcurement(id).then(setDoc).catch(() => setDoc(null));
  }, [id]);

  if (!doc) return null;

  return (
    <div data-testid="consumables-procurement-detail">
      <PageHeader
        title={doc.referenceNumber}
        meta={<span>{doc.source === 'xero' ? 'Xero' : 'Manual'}</span>}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            disabled
            title={t('common.comingSoon', { defaultValue: 'Coming soon' })}
          >
            {t('consumables.procurement.detail.edit')}
          </button>
        }
      />

      <FormSection id="transaction" title={t('consumables.procurement.detail.section.transaction')}>
        <DefinitionList columns={2}>
          <DataPair label={t('consumables.procurement.list.columns.date')} value={formatDate(doc.date)} />
          <DataPair label={t('consumables.procurement.list.columns.supplier')} value={doc.supplier} />
          <DataPair
            label={t('consumables.procurement.list.columns.product')}
            // Populate returns `null` only for true orphan rows (hard-deleted productId).
            // Soft-deleted products populate with deletedAt set — render "<name> (deleted)"
            // to preserve the historical name on this ledger surface.
            value={
              doc.productId
                ? doc.productId.deletedAt
                  ? `${doc.productId.productName} ${t('consumables.shared.deletedSuffix', { defaultValue: '(deleted)' })}`
                  : doc.productId.productName
                : t('consumables.shared.deletedProduct', { defaultValue: '(deleted product)' })
            }
          />
          <DataPair label={t('consumables.procurement.list.columns.quantity')} value={`${doc.quantity} ${doc.quantityUnit}`} />
        </DefinitionList>
      </FormSection>

      <FormSection id="financials" title={t('consumables.procurement.detail.section.financials')}>
        <DefinitionList columns={2}>
          <DataPair label={t('consumables.procurement.list.columns.unitPrice')} value={doc.unitPrice.toFixed(2)} />
          <DataPair label={t('consumables.procurement.list.columns.totalPrice')} value={doc.totalPrice.toFixed(2)} />
        </DefinitionList>
      </FormSection>

      <FormSection id="attachments" title={t('consumables.procurement.detail.section.attachments')}>
        {!doc.attachments?.length ? <em>—</em> : (
          <ul>
            {doc.attachments.map(a => (
              <li key={a._id}>
                <button
                  type="button"
                  onClick={() => handleOpenAttachment(a._id)}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--bs-primary)' /* no design-system link-button primitive; reuse the Bootstrap primary token */, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {a.fileName}
                </button>
              </li>
            ))}
          </ul>
        )}
        {attachmentError && (
          <Notice tone="danger" className="mt-2">
            {attachmentError}
          </Notice>
        )}
      </FormSection>

      <FormSection id="audit" title={t('consumables.procurement.detail.section.audit')}>
        <DefinitionList columns={2}>
          <DataPair label="Source" value={doc.source} />
          <DataPair label="Reference number" value={doc.referenceNumber} />
          {doc.xeroInvoiceId ? <DataPair label="Xero Invoice ID" value={doc.xeroInvoiceId} /> : null}
          <DataPair label="Created" value={formatDateTime(doc.createdAt)} />
        </DefinitionList>
      </FormSection>
    </div>
  );
};

export default ConsumableProcurementDetailPage;
